/**
 * Utility functions for date and time formatting
 */

/**
 * Formats a date as "Monday 10/14"
 * @param date - Date object to format
 * @returns Formatted string like "Monday 10/14"
 */
export function formatDateShort(date: Date): string {
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${dayOfWeek} ${month}/${day}`;
}

/**
 * Formats a date as "Monday • October 14" (for display)
 * @param date - Date object to format
 * @returns Formatted string like "Monday • October 14"
 */
export function formatDateDisplay(date: Date): string {
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();

  return `${dayOfWeek} • ${month} ${day}`;
}

/**
 * Formats time as "7:00 PM" or "7 PM" if on the hour
 * @param date - Date object to format
 * @returns Formatted time string like "7:00 PM" or "7 PM"
 */
export function formatTime(date: Date): string {
  // Format with options: "7:00 PM"
  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  // Remove :00 if time is on the hour, e.g. "7:00 PM" => "7 PM"
  return timeString.replace(/:00(\s*[AP]M)$/, '$1');
}

/**
 * Formats a price number as currency string
 * @param amount - Numeric amount (e.g., 15, 20.50) or null/undefined for free
 * @returns Formatted string like "$15", "$20.50", or "$FREE.99" for free classes
 */
export function formatPrice(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || amount === 0) {
    return "$FREE.99";
  }
  return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
}

/**
 * Builds a Venmo payment note with date and class title
 * Format: "John Doe\nMonday\n10/14\nBeginner_Hip_Hop" (if name provided)
 * Format: "Monday\n10/14\nBeginner_Hip_Hop" (if no name)
 * Format: "...\nABC123" (if paymentCode provided — appended at the end)
 *
 * @param date - Date object for the class
 * @param title - Class title (spaces will be replaced with underscores)
 * @param name - Optional registrant name to include at the top
 * @param paymentCode - Optional payment code to append at the end (for Venmo auto-detect)
 * @returns Formatted note string
 */
export function buildVenmoPaymentNote(date: Date, title: string, name?: string, paymentCode?: string): string {
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dateStr = `${month}/${day}`;

  // Replace spaces with underscores in the title
  const formattedTitle = title.replace(/\s+/g, '_');

  const lines = [dayOfWeek, dateStr, formattedTitle];

  // Add name at the beginning if provided
  if (name) {
    const formattedName = name.replace(/\s+/g, '_');
    lines.unshift(formattedName);
  }

  // Append payment code at the end if provided
  if (paymentCode) {
    lines.push(paymentCode);
  }

  return lines.join('\n');
}
